import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { API, apiClient } from '../api_clients';
import { useOrganizationStore } from '../stores/organization-store';
import { extractErrorMessage } from './axiosIntance';
import { mapApiOrganizationToEntity } from '../mappers/api-to-entity';
import { toast } from 'sonner';

// Re-export organization types from API
export type Organization = API.ResponsesOrganization;
export type CreateOrganizationRequest = API.CreateOrganizationRequest;
export type UpdateOrganizationRequest = API.UpdateOrganizationRequest;
export type OrganizationResponse = API.ResponsesOrganization | undefined;

// ============================================================================
// API Functions
// ============================================================================

/** 
 * Get all organizations
*/
export async function getOrganizations(): Promise<API.ResponsesOrganization[]> {
    const response = await apiClient.usersApi.getMyOrganizations("");
    return response.data.data?.organizations ?? [];
}

/**
 * Get organization by ID
 */
async function getOrganization(
    organizationUnid: string
): Promise<OrganizationResponse> {
    const response = await apiClient.organizationsApi.getOrganization(
        organizationUnid,
        ""
    );
    return response.data.data?.organization;
}

/**
 * Create a new organization
 */
async function createOrganization(
    data: CreateOrganizationRequest
): Promise<OrganizationResponse> {
    const response = await apiClient.organizationsApi.createOrganization(
        "",
        data
    );
    return response.data.data?.organization;
}

/**
 * Update an existing organization
 */
async function updateOrganization(
    organizationUnid: string,
    data: UpdateOrganizationRequest
): Promise<OrganizationResponse> {
    const response = await apiClient.organizationsApi.updateOrganization(
        organizationUnid,
        "",
        data
    );
    return response.data.data?.organization;
}

/**
 * Delete an organization
 */
async function deleteOrganization(organizationUnid: string): Promise<void> {
    await apiClient.organizationsApi.deleteOrganization(organizationUnid, "");
}

// ============================================================================
// React Query Hooks
// ============================================================================

/**
 * Hook to get all organizations
 */
export function useGetOrganizations(enabled: boolean = true) {
    const query = useQuery({
        queryKey: ['organizations'],
        queryFn: () => getOrganizations(),
        staleTime: 1000 * 60 * 5, // 5 minutes
        retry: false,
        enabled: enabled
    });

    useEffect(() => {
        if (query.error) {
            const errorMessage = extractErrorMessage(query.error);
            toast.error('Failed to fetch organizations', {
                description: errorMessage,
            });
        }
    }, [query.error]);
    return query;
}

/**
 * Hook to get organization by ID
 */
export function useGetOrganization(
    organizationUnid: string,
    enabled: boolean = true
) {
    const query = useQuery({
        queryKey: ['organization', organizationUnid],
        queryFn: () => getOrganization(organizationUnid),
        enabled: enabled && !!organizationUnid,
        staleTime: 1000 * 60 * 5, // 5 minutes
        retry: false
    });

    // Handle error with useEffect
    useEffect(() => {
        if (query.error) {
            const errorMessage = extractErrorMessage(query.error);
            toast.error('Failed to fetch organization', {
                description: errorMessage,
            });
        }
    }, [query.error]);

    return query;
}

/**
 * Hook to create a new organization
 */
export function useCreateOrganization() {
    const { setOrganization } = useOrganizationStore();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: createOrganization,
        onSuccess: (data) => {
            // 1. Update store if data exists
            if (data) {
                const organization = mapApiOrganizationToEntity(data);
                setOrganization(organization);
            }

            // 2. Invalidate organizations queries to refetch
            queryClient.invalidateQueries({ queryKey: ['organizations'] });
            queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });

            // 3. Show success message
            toast.success('Organization created successfully!');
        },
        onError: (error) => {
            const errorMessage = extractErrorMessage(error);
            toast.error('Failed to create organization', {
                description: errorMessage,
            });
        },
    });
}

/**
 * Hook to update an organization
 */
export function useUpdateOrganization() {
    const { organization, setOrganization } = useOrganizationStore();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: UpdateOrganizationRequest) => {
            if (!organization?.id) {
                throw new Error('No organization selected');
            }
            return updateOrganization(organization.id, data);
        },
        onSuccess: (data) => {
            // 1. Update store if data exists
            if (data) {
                const updatedOrganization = mapApiOrganizationToEntity(data);
                setOrganization(updatedOrganization);
            }

            // 2. Invalidate specific organization and list queries
            queryClient.invalidateQueries({ queryKey: ['organizations'] });
            if (organization?.id) {
                queryClient.invalidateQueries({
                    queryKey: ['organization', organization.id],
                });
            }
            queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });

            // 3. Show success message
            toast.success('Organization updated successfully!');
        },
        onError: (error) => {
            const errorMessage = extractErrorMessage(error);
            toast.error('Failed to update organization', {
                description: errorMessage,
            });
        },
    });
}

/**
 * Hook to delete an organization
 */
export function useDeleteOrganization() {
    const { setOrganization } = useOrganizationStore();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: deleteOrganization,
        onSuccess: (_, organizationUnid) => {
            // 1. Clear organization from store
            setOrganization(null);

            // 2. Invalidate all organization queries
            queryClient.invalidateQueries({ queryKey: ['organizations'] });
            queryClient.invalidateQueries({ queryKey: ['organization', organizationUnid] });
            queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });

            // 3. Show success message
            toast.success('Organization deleted successfully!');
        },
        onError: (error) => {
            const errorMessage = extractErrorMessage(error);
            toast.error('Failed to delete organization', {
                description: errorMessage,
            });
        },
    });
}
